// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { useApp } from '../../store/AppContext.js';
import { getAccountTransactions, formatAmount } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';
import {
  calculateCostBasis,
  calculateTotalCostBasis,
  calculateSharesHeld,
  calculateMarketValue,
  calculateUnrealizedGain,
  getLatestPrice,
  getCommodityId,
  getInvestmentAction,
  formatShares,
  INVESTMENT_ACCOUNT_TYPES,
} from '../../lib/investment.js';

type DateRange = '1M' | '3M' | '6M' | 'YTD' | '1Y' | 'All';
type SortKey = 'symbol' | 'value' | 'gain' | 'pct';

const CHART_COLORS = [
  '#4A7FA5', '#3D9970', '#C0392B', '#E67E22', '#9B59B6',
  '#1ABC9C', '#F39C12', '#E74C3C', '#2ECC71', '#3498DB',
];

export function Portfolio() {
  const { t } = useTranslation(['investment', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();

  const [sortKey, setSortKey] = useState<SortKey>('value');
  const [sortAsc, setSortAsc] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>('1Y');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);
  const allTransactions = useMemo(() => [...ledger.transactions.values()], [ledger.transactions]);

  const investmentAccounts = useMemo(
    () => [...ledger.accounts.values()].filter((a) => INVESTMENT_ACCOUNT_TYPES.has(a.type) && !a.placeholder),
    [ledger.accounts],
  );

  const holdings = useMemo(() => {
    return investmentAccounts.map((account) => {
      const accountTxs = getAccountTransactions(ledger, account.id);
      const sharesHeld = calculateSharesHeld(account.id, accountTxs);
      const lots = calculateCostBasis(account.id, accountTxs);
      const costBasis = calculateTotalCostBasis(lots);
      const commodityId = getCommodityId(account);
      const latestPrice = getLatestPrice(commodityId, today, allPrices);
      const marketValue = latestPrice ? calculateMarketValue(sharesHeld, latestPrice.value) : null;
      const unrealized = marketValue !== null ? calculateUnrealizedGain(marketValue, costBasis) : null;

      return {
        account,
        sharesHeld,
        costBasis,
        marketValue,
        latestPrice,
        unrealized,
        commodityId,
      };
    });
  }, [investmentAccounts, ledger, allPrices, today]);

  const totalMarketValue = holdings.reduce((s, h) => s + (h.marketValue ?? 0), 0);
  const totalCostBasis = holdings.reduce((s, h) => s + h.costBasis, 0);
  const totalUnrealizedGain = totalMarketValue - totalCostBasis;
  const totalUnrealizedPct = totalCostBasis !== 0 ? totalUnrealizedGain / totalCostBasis : 0;

  const sortedHoldings = useMemo(() => {
    const arr = [...holdings];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'symbol': cmp = (a.account.commodity?.ticker ?? '').localeCompare(b.account.commodity?.ticker ?? ''); break;
        case 'value': cmp = (a.marketValue ?? 0) - (b.marketValue ?? 0); break;
        case 'gain': cmp = (a.unrealized?.amount ?? 0) - (b.unrealized?.amount ?? 0); break;
        case 'pct': cmp = (a.unrealized?.percentage ?? 0) - (b.unrealized?.percentage ?? 0); break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [holdings, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return null;
    return <span className="text-accent ml-0.5">{sortAsc ? '↑' : '↓'}</span>;
  }

  // Pie chart data
  const pieData = useMemo(() => {
    const withValue = sortedHoldings.filter((h) => (h.marketValue ?? 0) > 0);
    return withValue.map((h) => ({
      name: h.account.commodity?.ticker ?? h.account.name,
      value: h.marketValue ?? 0,
      accountId: h.account.id,
    }));
  }, [sortedHoldings]);

  // Performance chart data
  const performanceData = useMemo(() => {
    const now = new Date();
    const rangeMap: Record<DateRange, number> = {
      '1M': 1, '3M': 3, '6M': 6, 'YTD': 0, '1Y': 12, 'All': 60,
    };

    let startDate: Date;
    if (dateRange === 'YTD') {
      startDate = new Date(now.getFullYear(), 0, 1);
    } else {
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - rangeMap[dateRange]);
    }
    if (dateRange === 'All' && allTransactions.length > 0) {
      const oldest = allTransactions
        .filter((t) => !t.voided)
        .map((t) => t.date)
        .sort()[0];
      if (oldest) startDate = new Date(oldest + 'T00:00:00');
    }

    const months: string[] = [];
    const d = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (d <= now) {
      months.push(d.toISOString().slice(0, 7));
      d.setMonth(d.getMonth() + 1);
    }

    return months.map((ym) => {
      const endOfMonth = ym + '-31';
      let totalValue = 0;
      let totalBasis = 0;
      let interpolated = false;

      for (const account of investmentAccounts) {
        const accountTxs = getAccountTransactions(ledger, account.id).filter((t) => t.date <= endOfMonth);
        const shares = calculateSharesHeld(account.id, accountTxs);
        const lots = calculateCostBasis(account.id, accountTxs);
        const basis = calculateTotalCostBasis(lots);
        totalBasis += basis;

        const commodityId = getCommodityId(account);
        const price = getLatestPrice(commodityId, endOfMonth, allPrices);
        if (price) {
          totalValue += calculateMarketValue(shares, price.value);
          if (price.date < ym + '-01') interpolated = true;
        } else {
          totalValue += basis; // fallback to cost basis
          if (basis > 0) interpolated = true;
        }
      }

      return {
        month: ym,
        label: formatDate(ym + '-15', { month: 'short', year: '2-digit' }),
        value: totalValue / 100,
        basis: totalBasis / 100,
        interpolated,
      };
    });
  }, [investmentAccounts, ledger, allPrices, allTransactions, dateRange]);

  // Recent investment transactions
  const recentActivity = useMemo(() => {
    const items: { date: string; accountName: string; description: string; action: string; shares: number; value: number }[] = [];
    for (const account of investmentAccounts) {
      const txs = getAccountTransactions(ledger, account.id);
      for (const tx of txs) {
        const split = tx.splits.find((s) => s.accountId === account.id);
        const action = getInvestmentAction(tx, account.id);
        items.push({
          date: tx.date,
          accountName: account.commodity?.ticker ?? account.name,
          description: tx.description,
          action: action,
          shares: split?.quantity ?? 0,
          value: Math.abs(split?.amount ?? 0),
        });
      }
    }
    items.sort((a, b) => b.date.localeCompare(a.date));
    return items.slice(0, 10);
  }, [investmentAccounts, ledger]);

  const hasInterpolatedData = performanceData.some((d) => d.interpolated);

  function formatDollar(v: number) {
    return `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('portfolio.title')}</h2>
        <span className="text-xs text-text-muted">{t('portfolio.asOf', { date: formatDate(new Date(), { month: 'short', day: 'numeric', year: 'numeric' }) })}</span>
      </div>

      {/* Summary header */}
      <div className="grid grid-cols-3 gap-0 border border-border mb-4 bg-bg-surface">
        <div className="p-4 border-r border-border">
          <p className="text-xs text-text-muted mb-1">{t('portfolio.marketValue')}</p>
          <p className="text-xl font-mono font-semibold text-text">{formatAmount(totalMarketValue, 'USD')}</p>
        </div>
        <div className="p-4 border-r border-border">
          <p className="text-xs text-text-muted mb-1">{t('portfolio.totalCostBasis')}</p>
          <p className="text-xl font-mono font-semibold text-text">{formatAmount(totalCostBasis, 'USD')}</p>
        </div>
        <div className="p-4">
          <p className="text-xs text-text-muted mb-1">{t('portfolio.unrealizedGainLoss')}</p>
          <p className={`text-xl font-mono font-semibold ${totalUnrealizedGain >= 0 ? 'text-positive' : 'text-negative'}`}>
            {totalUnrealizedGain >= 0 ? '+' : ''}{formatAmount(totalUnrealizedGain, 'USD')}
            <span className="text-sm ml-2">
              ({totalUnrealizedGain >= 0 ? '+' : ''}{(totalUnrealizedPct * 100).toFixed(1)}%)
            </span>
          </p>
        </div>
      </div>

      {investmentAccounts.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-text-muted">
          <div className="text-center">
            <p className="text-sm">{t('portfolio.noAccounts')}</p>
            <p className="text-xs text-text-subtle mt-1">{t('portfolio.createPrompt')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Holdings table */}
          <div className="border border-border mb-4">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-bg-surface border-b border-border-strong">
                <tr>
                  <th
                    className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3 cursor-pointer hover:text-text"
                    onClick={() => toggleSort('symbol')}
                  >
                    {t('portfolio.columns.symbol')} {sortIndicator('symbol')}
                  </th>
                  <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3">{t('portfolio.columns.name')}</th>
                  <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3">{t('portfolio.columns.shares')}</th>
                  <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3">{t('portfolio.columns.price')}</th>
                  <th
                    className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3 cursor-pointer hover:text-text"
                    onClick={() => toggleSort('value')}
                  >
                    {t('portfolio.columns.value')} {sortIndicator('value')}
                  </th>
                  <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3">{t('portfolio.columns.cost')}</th>
                  <th
                    className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3 cursor-pointer hover:text-text"
                    onClick={() => toggleSort('gain')}
                  >
                    {t('portfolio.columns.gainLoss')} {sortIndicator('gain')}
                  </th>
                  <th
                    className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-3 cursor-pointer hover:text-text"
                    onClick={() => toggleSort('pct')}
                  >
                    {t('portfolio.columns.percent')} {sortIndicator('pct')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedHoldings.map((h, idx) => {
                  const isHighlighted = highlightedId === h.account.id;
                  return (
                    <tr
                      key={h.account.id}
                      className={`border-b border-border cursor-pointer hover:bg-bg-elevated transition-colors ${
                        isHighlighted ? 'bg-accent-muted' : idx % 2 === 0 ? 'bg-bg' : 'bg-bg-surface'
                      }`}
                      onClick={() => navigate(`/accounts/${h.account.id}`)}
                    >
                      <td className="py-2 px-3 font-mono text-xs text-text font-medium">
                        {h.account.commodity?.ticker ?? '—'}
                      </td>
                      <td className="py-2 px-3 text-text-muted text-xs">
                        {h.account.commodity?.fullName ?? h.account.name}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-text">
                        {formatShares(h.sharesHeld)}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-text-muted">
                        {h.latestPrice
                          ? `$${(h.latestPrice.value / 100).toFixed(2)}`
                          : <span className="text-text-subtle">{t('portfolio.noPrice')}</span>}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-text">
                        {h.marketValue !== null ? formatAmount(h.marketValue, h.account.currency) : '—'}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs text-text-muted">
                        {formatAmount(h.costBasis, h.account.currency)}
                      </td>
                      <td className={`py-2 px-3 text-right font-mono text-xs ${
                        h.unrealized === null ? 'text-text-subtle' :
                        h.unrealized.amount >= 0 ? 'text-positive' : 'text-negative'
                      }`}>
                        {h.unrealized !== null
                          ? `${h.unrealized.amount >= 0 ? '+' : ''}${formatAmount(h.unrealized.amount, h.account.currency)}`
                          : '—'}
                      </td>
                      <td className={`py-2 px-3 text-right font-mono text-xs ${
                        h.unrealized === null ? 'text-text-subtle' :
                        h.unrealized.percentage >= 0 ? 'text-positive' : 'text-negative'
                      }`}>
                        {h.unrealized !== null
                          ? `${h.unrealized.percentage >= 0 ? '+' : ''}${(h.unrealized.percentage * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {sortedHoldings.length > 0 && (
                <tfoot className="border-t border-border-strong bg-bg-surface">
                  <tr>
                    <td colSpan={4} className="py-2 px-3 text-xs font-medium text-text-muted">{t('portfolio.total')}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs text-text font-medium">
                      {formatAmount(totalMarketValue, 'USD')}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs text-text-muted">
                      {formatAmount(totalCostBasis, 'USD')}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono text-xs font-medium ${totalUnrealizedGain >= 0 ? 'text-positive' : 'text-negative'}`}>
                      {totalUnrealizedGain >= 0 ? '+' : ''}{formatAmount(totalUnrealizedGain, 'USD')}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono text-xs font-medium ${totalUnrealizedPct >= 0 ? 'text-positive' : 'text-negative'}`}>
                      {totalUnrealizedPct >= 0 ? '+' : ''}{(totalUnrealizedPct * 100).toFixed(1)}%
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Charts */}
          {pieData.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Allocation pie chart */}
              <div className="border border-border bg-bg-surface p-4">
                <h3 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-3">{t('portfolio.allocation')}</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onClick={(d: any) => { if (d?.accountId) setHighlightedId(d.accountId as string); }}
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length] ?? '#4A7FA5'}
                          opacity={highlightedId && highlightedId !== entry.accountId ? 0.4 : 1}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#222535', border: '1px solid #2A2D3A', borderRadius: 0 }}
                      formatter={(v) => [formatDollar(Number(v) / 100)]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, color: '#7B7F9E' }}
                      formatter={(name, entry) => {
                        const pct = totalMarketValue > 0 ? ((entry.payload as { value: number }).value / totalMarketValue * 100).toFixed(1) : '0';
                        return `${name} ${pct}%`;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Performance chart */}
              <div className="border border-border bg-bg-surface p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-text-muted uppercase tracking-wide">{t('portfolio.performance')}</h3>
                  <div className="flex gap-1">
                    {(['1M', '3M', '6M', 'YTD', '1Y', 'All'] as DateRange[]).map((r) => (
                      <button
                        key={r}
                        onClick={() => setDateRange(r)}
                        className={`px-1.5 py-0.5 text-xs border transition-colors ${
                          dateRange === r
                            ? 'border-accent text-accent bg-accent-muted'
                            : 'border-border text-text-muted hover:border-border-strong hover:text-text'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={performanceData} margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2D3A" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#7B7F9E' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatDollar} tick={{ fontSize: 10, fill: '#7B7F9E' }} width={70} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: '#222535', border: '1px solid #2A2D3A', borderRadius: 0 }}
                      formatter={(v) => [formatDollar(Number(v))]}
                      labelStyle={{ color: '#E8E9F0', fontWeight: 600 }}
                    />
                    <Line type="monotone" dataKey="value" stroke="#4A7FA5" strokeWidth={2} dot={false} name={t('portfolio.marketValue')} isAnimationActive={false} />
                    <Line type="monotone" dataKey="basis" stroke="#7B7F9E" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name={t('portfolio.costBasis')} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
                {hasInterpolatedData && (
                  <p className="text-xs text-text-subtle text-center mt-1">* {t('portfolio.estimateNote')}</p>
                )}
              </div>
            </div>
          )}

          {/* Recent activity */}
          {recentActivity.length > 0 && (
            <div className="border border-border bg-bg-surface">
              <div className="px-4 py-2 border-b border-border">
                <h3 className="text-xs font-medium text-text-muted uppercase tracking-wide">{t('portfolio.recentActivity')}</h3>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-text-subtle">
                    <th className="text-left py-1.5 px-3 font-medium">{t('register.columns.date')}</th>
                    <th className="text-left py-1.5 px-3 font-medium">{t('register.columns.account')}</th>
                    <th className="text-left py-1.5 px-3 font-medium">{t('register.columns.description')}</th>
                    <th className="text-center py-1.5 px-3 font-medium">{t('portfolio.columns.action')}</th>
                    <th className="text-right py-1.5 px-3 font-medium">{t('portfolio.columns.shares')}</th>
                    <th className="text-right py-1.5 px-3 font-medium">{t('portfolio.columns.value')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((item, idx) => (
                    <tr key={idx} className={`border-b border-border ${idx % 2 === 0 ? 'bg-bg' : 'bg-bg-surface'}`}>
                      <td className="py-1.5 px-3 font-mono text-text-muted">{item.date}</td>
                      <td className="py-1.5 px-3 font-mono font-medium text-text">{item.accountName}</td>
                      <td className="py-1.5 px-3 text-text-muted">{item.description}</td>
                      <td className={`py-1.5 px-3 text-center font-medium ${
                        item.action === 'buy' ? 'text-accent' :
                        item.action === 'sell' ? 'text-text-muted' :
                        'text-positive'
                      }`}>
                        {item.action.toUpperCase()}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-text">
                        {item.shares !== 0 ? `${item.shares > 0 ? '+' : ''}${formatShares(item.shares)}` : ''}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-text">
                        {formatAmount(item.value, 'USD')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
